import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { MessageDescriptor } from 'shared/i18n';
import { AppDispatch } from 'store/store';
import { Divider } from 'ud-ui/components';
import UDInput from 'ud-form/components/input/component';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import { getGameEventTypeLabel } from 'modules/games/domain/constants/game-event-type-labels';
import { GameDetailsTeam, GameDetailsTeamMember, GameEvent } from 'modules/games/domain/interfaces/game-details';
import { getPlayerPositionLabel } from 'modules/teams/domain/constants/player-position-labels';
import { setMomentUrl } from 'modules/games/store/game-details';
import { GameResultEventIcon } from 'modules/games/ui/components/game-result-events/game-result-event';
import { EventCardWrapper, EventIcon, PlayerInfo, teamLogoStyles, TimeInfo } from './styles';

interface IProps {
  event: GameEvent;
  teams: [GameDetailsTeam, GameDetailsTeam];
  members: Record<number, GameDetailsTeamMember>;
}

function EventCard(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();
  const { event, members, teams } = props;
  const eventTypeLabel = getGameEventTypeLabel(event.type);
  const positionName = (position?: string) => {
    const label = getPlayerPositionLabel(position);
    return label ? t(label) : position;
  };
  const periodLabel = PERIOD_LABELS[event.period];
  const minutes = event.minutes;
  const extraMinutes = event.extraMinutes;

  const handleChangeUrl = useCallback(
    (value: string) => {
      dispatch(setMomentUrl({ eventId: event.id, url: value }));
    },
    [dispatch, event.id],
  );

  return (
    <EventCardWrapper>
      <TeamLogo size={80} url={teams.find((team) => team.id === event.teamId)?.logo} style={teamLogoStyles} />
      <Divider type={'vertical'} color={'#000000'} width={'1px'} />
      <EventIcon>
        <GameResultEventIcon type={event.type} />
      </EventIcon>
      <Divider type={'vertical'} color={'#000000'} width={'1px'} />
      <PlayerInfo>
        <div>{eventTypeLabel ? t(eventTypeLabel) : event.type}</div>
        <div className={'flex-center h-full'} style={{ gap: 16 }}>
          {event.playerIds.map((playerId) => (
            <div key={playerId}>
              <div className="game-event-player-info">
                {members[playerId]?.lastName ?? t`Неизвестный`} {members[playerId]?.firstName ?? t`Неизвестный`}
              </div>
              <div className="game-event-player-position">
                #{members[playerId]?.number ?? '?'}, {members[playerId] ? positionName(members[playerId]?.position) : t`Неизвестно`}
              </div>
            </div>
          ))}
        </div>
      </PlayerInfo>
      <Divider type={'vertical'} color={'#000000'} width={'1px'} />
      <TimeInfo>
        <div className="game-event-period">{periodLabel ? t(periodLabel) : undefined}</div>
        <div className={'flex-center h-full flex-col'}>
          <div className="game-event-minutes">
            <Trans>{minutes}` минута</Trans>
          </div>
          {extraMinutes && <div className="game-event-extra-minutes">{t`+${extraMinutes} доп.`}</div>}
        </div>
      </TimeInfo>
      <div className={'flex w-full flex-col justify-between'} style={{ padding: 8 }}>
        <UDText type={'subhead'}>
          <Trans>Ссылка на момент</Trans>
        </UDText>
        <UDInput value={event?.videoUrl} placeholder={t`Введите ссылку`} onChange={(event) => handleChangeUrl(event.target.value)} />
      </div>
    </EventCardWrapper>
  );
}

export default EventCard;

const PERIOD_LABELS: Record<number, MessageDescriptor> = {
  0: msg`Серия пенальти`,
  1: msg`1-й тайм`,
  2: msg`2-й тайм`,
};
